import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserProfile,SubmitAttemptResponse } from '../../models';

const STORAGE_KEY = 'cinephile_user_id';
const SELECTED_QUIZ_KEY = 'cinephile_selected_quiz_id';
const ATTEMPT_KEY = 'cinephile_attempt_id';
const PROFILES_KEY = 'cinephile_profiles';
const DEFAULT_USER_ID = 'guest_user';

@Injectable({ providedIn: 'root' })
export class AppStateService {
  // ── Current user ─────────────────────────────────
  private _userId = new BehaviorSubject<string>(
    this.readStoredUserId()
  );
  userId$ = this._userId.asObservable();

  private _user = new BehaviorSubject<UserProfile | null>(
    this.readStoredUser(this.readStoredUserId())
  );
  user$ = this._user.asObservable();

  // ── Quiz session ──────────────────────────────────
  private _selectedQuizId = new BehaviorSubject<string | null>(
    this.readSessionValue(SELECTED_QUIZ_KEY)
  );
  selectedQuizId$ = this._selectedQuizId.asObservable();

  private _attemptId = new BehaviorSubject<string | null>(
    this.readSessionValue(ATTEMPT_KEY)
  );
  attemptId$ = this._attemptId.asObservable();

  private _lastResult = new BehaviorSubject<SubmitAttemptResponse | null>(null);
  lastResult$ = this._lastResult.asObservable();

  // ── Ticket animation trigger ──────────────────────
  private _ticketFlash = new BehaviorSubject<number>(0);
  ticketFlash$ = this._ticketFlash.asObservable();

  // ── Getters ───────────────────────────────────────
  get userId(): string { return this._userId.value; }
  get user(): UserProfile | null { return this._user.value; }
  get selectedQuizId(): string | null { return this._selectedQuizId.value; }
  get attemptId(): string | null { return this._attemptId.value; }
  get lastResult(): SubmitAttemptResponse | null { return this._lastResult.value; }

  // ── Setters ───────────────────────────────────────
  setUserId(id: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, id);
    }
    this._userId.next(id);
    const existing = this.readStoredUser(id);
    if (existing) {
      this._user.next(existing);
    }
  }

  setUser(user: UserProfile | null): void {
    this._user.next(user);
    if (user) {
      this.persistProfile(user);
    }
  }

  setSelectedQuiz(quizId: string): void {
    this.writeSessionValue(SELECTED_QUIZ_KEY, quizId);
    this._selectedQuizId.next(quizId);
  }

  setAttemptId(id: string): void {
    this.writeSessionValue(ATTEMPT_KEY, id);
    this._attemptId.next(id);
  }

  setLastResult(result: SubmitAttemptResponse): void {
    this._lastResult.next(result);
  }

  flashTickets(amount: number): void {
    this._ticketFlash.next(amount);
  }

  updateUserTickets(newBalance: number): void {
    const u = this._user.value;
    if (u) {
      const updated: UserProfile = {
        ...u,
        tickets: newBalance,
        total_tickets_earned: Math.max(u.total_tickets_earned || 0, newBalance)
      };
      this._user.next(updated);
      this.persistProfile(updated);
    }
  }

  private persistProfile(profile: UserProfile): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(PROFILES_KEY);
      const all = raw ? JSON.parse(raw) : {};
      all[profile.user_id] = profile;
      localStorage.setItem(PROFILES_KEY, JSON.stringify(all));
    } catch (_) {}
  }

  clearSession(): void {
    this.writeSessionValue(SELECTED_QUIZ_KEY, null);
    this.writeSessionValue(ATTEMPT_KEY, null);
    this._selectedQuizId.next(null);
    this._attemptId.next(null);
    this._lastResult.next(null);
  }

  private readStoredUserId(): string {
    if (typeof localStorage === 'undefined') return DEFAULT_USER_ID;
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_USER_ID;
  }

  private readStoredUser(userId: string): UserProfile | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(PROFILES_KEY);
      if (!raw) return null;
      const all = JSON.parse(raw);
      return all[userId] || null;
    } catch (_) {
      return null;
    }
  }

  private readSessionValue(key: string): string | null {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(key);
  }

  private writeSessionValue(key: string, value: string | null): void {
    if (typeof sessionStorage === 'undefined') return;

    if (value) {
      sessionStorage.setItem(key, value);
      return;
    }

    sessionStorage.removeItem(key);
  }
}
